import { Injectable } from '@angular/core';
import { head } from 'lodash';
import { AllowedOrgUnit, Combinator, CompositePredicate, Predicate, SimplePredicate, isCompositePredicate, isSimplePredicate } from './filter.type';

@Injectable({
    providedIn: 'root'
})
export class PredicatesService {

    removePredicate(predicates: Predicate[], id: string, is_recursif: boolean = false) {
        if (predicates.length === 1 && predicates[0].id === id) {
            return [];
        }

        const res = predicates.map(predicate => {
            if (isCompositePredicate(predicate) && predicate.predicates) {
                const is_found = predicate.predicates.some(p => p.id === id);

                if (is_found) {
                    predicate.predicates = predicate.predicates.filter(p => p.id !== id)
                } else {
                    predicate.predicates = this.removePredicate(predicate.predicates, id, true);
                }

                if (is_recursif && predicate.predicates.length === 1) {
                    return predicate.predicates[0];
                }

                if (predicate.predicates.length === 0) {
                    return null;
                }
            }

            return predicate;
        });

        return res.filter(predicate => predicate != null);
    }

    addOrgUnitType(
        new_org_unit: SimplePredicate,
        predicates: CompositePredicate[],
        uniqueIdFn = uniqueId
    ): CompositePredicate[] {
        const root: CompositePredicate = head(predicates);

        if (!root) {
            return [{
                combinator: "OR",
                id: uniqueIdFn(),
                predicates: [new_org_unit]
            }]
        } else if (root.predicates) {
            root.predicates.push(new_org_unit);
            return [root];
        } else {
            return [{
                combinator: "AND",
                id: uniqueIdFn(),
                predicates: [root, new_org_unit]
            }]
        }
    }

    addFilterInGroup(predicates: Predicate[], id: string, value: SimplePredicate, uniqueIdFn = uniqueId) {
        return predicates.map(predicate => {
            if (predicate.id === id && isCompositePredicate(predicate)) {
                predicate.predicates.push(value);
                return predicate;
            }
            else if (predicate.id === id) {
                predicate = {
                    combinator: "AND",
                    id: uniqueIdFn(),
                    predicates: [predicate, value]
                };
                return predicate;
            } else if (isCompositePredicate(predicate)) {
                predicate.predicates = this.addFilterInGroup(predicate.predicates, id, value, uniqueIdFn);
                return predicate;
            } else {
                return predicate;
            }
        });
    }

    updatePredicate(predicates: Predicate[], id: string, value: AllowedOrgUnit | Combinator) {
        return predicates.map(predicate => {
            if (predicate.id === id && isCompositePredicate(predicate)) {
                predicate.combinator = value as Combinator;
                return predicate;
            } else if (predicate.id === id && isSimplePredicate(predicate)) {
                const orgUnit = value as AllowedOrgUnit;
                predicate['org_unit_value'] = orgUnit.name;
                predicate['id'] = orgUnit.id;
                return predicate;
            } else if (isCompositePredicate(predicate)) {
                predicate.predicates = this.updatePredicate(predicate.predicates, id, value);
                return predicate;
            } else {
                return predicate;
            }
        });
    }

    addMissingIdToPredicate<T extends Predicate>(predicates: T[]): T[] {
        return predicates.map(predicate => {
            if (!predicate.id) {
                predicate.id = predicate.id ? predicate.id : uniqueId();
            }

            if (isCompositePredicate(predicate)) {
                predicate.predicates = this.addMissingIdToPredicate(predicate.predicates);
            }

            return predicate;
        });
    }
}

/**
     * Creates an UUID that can be used for dynamic id attributes
     * @returns {string}
     */
export const uniqueId = (): string => {
    let d = new Date().getTime();//Timestamp
    let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = Math.random() * 16;//random number between 0 and 16
        if (d > 0) {//Use timestamp until depleted
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {//Use microseconds since page-load if supported
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
}